import { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { supabase } from "../../supabaseClient.js";
import useAlert from '../../alert/useAlert.js';
import { getVitalFlagsFromScan, getSiStatus, isSiCritical, isSiAbnormal } from '../MedicalScribe/Dashboard/mindvaultUtils';

const toISODate = (d) => new Date(d).toISOString();

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};
const endOfDay = (d) => {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
};

const dateRangeFromPreset = (preset, customFrom, customTo) => {
  const now = new Date();
  if (preset === 'custom' && customFrom && customTo) {
    return { start: startOfDay(customFrom), end: endOfDay(customTo) };
  }
  if (preset === 'month') {
    const start = new Date();
    start.setDate(start.getDate() - 30);
    return { start: startOfDay(start), end: endOfDay(now) };
  }
  if (preset === 'quarter') {
    const start = new Date();
    start.setDate(start.getDate() - 90);
    return { start: startOfDay(start), end: endOfDay(now) };
  }
  const start = new Date();
  start.setDate(start.getDate() - 7);
  return { start: startOfDay(start), end: endOfDay(now) };
};

const daysBetween = (start, end) => {
  const out = [];
  const cur = startOfDay(start);
  const last = startOfDay(end);
  while (cur <= last) {
    out.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
};

const parseJsonSafe = (value, fallback = {}) => {
  if (!value) return fallback;
  if (typeof value === 'object') return value;
  try { return JSON.parse(value); } catch { return fallback; }
};

export const useDashboardDataVitals = ({ enabled = true } = {}) => {
  const { userDetails } = useSelector(
    (state) => ({ userDetails: state.globalState.userDetails }),
    shallowEqual
  );
  const role = useSelector((state) => state.globalState.role, shallowEqual);
  const currentOrg = useSelector((state) => state.globalState.currentOrg, shallowEqual);
  const { setAlert } = useAlert();
  const hasMounted = useRef(false);

  const [loading, setLoading] = useState(false);

  const [timeRange, setTimeRange] = useState('week');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  // keep same names so DashboardView needs minimal/no UI changes
  const [matchedMindvaultIds, setMatchedMindvaultIds] = useState([]);
  const [activeMindvaultIds, setActiveMindvaultIds] = useState([]);

  const [scanCount, setScanCount] = useState(0);
  const [chatCount, setChatCount] = useState(0);
  const [appointmentCount, setAppointmentCount] = useState(0);

  const [scanSeries, setScanSeries] = useState([]);
  const [chatSeries, setChatSeries] = useState([]);

  const [riskCounts, setRiskCounts] = useState({ LOW: 0, MEDIUM: 0, HIGH: 0 });

  const { start, end } = useMemo(
    () => dateRangeFromPreset(timeRange, customFrom, customTo),
    [timeRange, customFrom, customTo]
  );

  const fetchDashboard = async () => {
    if (!enabled || !currentOrg?.id) return;

    setLoading(true);
    try {
      const [scanRes, chatRes] = await Promise.all([
        supabase
          .from('scan_history')
          .select(`
            id, session_id, team_id, user_id, created_at,
            heart_rate, sbp, dbp, oxygen_saturation, respiration_rate, stress_index, hr_variability,
            gender, height, weight, birth, user_tag
          `)
          .eq('team_id', currentOrg.id)
          .is('user_id', null)
          .gte('created_at', toISODate(start))
          .lte('created_at', toISODate(end)),
        supabase
          .from('chat_sessions')
          .select(`
            id, session_id, team_id, user_id, created_at,
            risk_level, risk_signals, user_details
          `)
          .eq('team_id', currentOrg.id)
          .is('user_id', null)
          .eq('is_deleted', false)
          .gte('created_at', toISODate(start))
          .lte('created_at', toISODate(end)),
      ]);

      if (scanRes.error) throw scanRes.error;
      if (chatRes.error) throw chatRes.error;

      const scans = scanRes.data || [];
      const chats = chatRes.data || [];

      const grouped = new Map();
      const scanKey = (s) => (s.session_id ? `session:${s.session_id}` : `scan:${s.id}`);
      const chatKey = (c) => (c.session_id ? `session:${c.session_id}` : `chat:${c.id}`);

      scans.forEach((s) => {
        const key = scanKey(s);
        if (!grouped.has(key)) grouped.set(key, { scans: [], chats: [], key });
        grouped.get(key).scans.push(s);
      });

      chats.forEach((c) => {
        const key = chatKey(c);
        if (!grouped.has(key)) grouped.set(key, { scans: [], chats: [], key });
        grouped.get(key).chats.push(c);
      });

      const records = Array.from(grouped.values()).map((g) => {
        const latestScan = [...g.scans].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
        const latestChat = [...g.chats].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
        return {
          key: g.key,
          scans: g.scans,
          chats: g.chats,
          latestScan,
          latestChat,
        };
      });

      const filteredRecords = records;

      const allowedKeys = new Set(filteredRecords.map((r) => r.key));

      const filteredScans = scans.filter((s) => allowedKeys.has(scanKey(s)));
      const filteredChats = chats.filter((c) => allowedKeys.has(chatKey(c)));

      // KPI counts
      const staffIds = filteredRecords.map((r) => r.key);
      setMatchedMindvaultIds(staffIds);
      setActiveMindvaultIds(staffIds);
      setScanCount(filteredScans.length);
      setChatCount(filteredChats.length);
      setAppointmentCount(0); // not used in standalone corporate-vitals flow

      // risk counts
      const counts = { LOW: 0, MEDIUM: 0, HIGH: 0 };

      filteredChats.forEach((c) => {
        const lvl = String(c.risk_level || '').toUpperCase();
        if (lvl === 'LOW' || lvl === 'MEDIUM' || lvl === 'HIGH') counts[lvl] += 1;
      });

      filteredScans.forEach((s) => {
        const flags = getVitalFlagsFromScan(s);
        counts.MEDIUM += (flags?.abnormal?.length || 0);
        counts.HIGH += (flags?.critical?.length || 0);

        const stress = getSiStatus(s.stress_index);
        if (isSiAbnormal(stress)) counts.MEDIUM += 1;
        if (isSiCritical(stress)) counts.HIGH += 1;
      });

      setRiskCounts(counts);

      // line series
      const days = daysBetween(start, end);
      const scanCountsByDay = new Map();
      const chatCountsByDay = new Map();

      filteredScans.forEach((s) => {
        const k = new Date(s.created_at).toLocaleDateString('en-US', { month: 'short', day: '2-digit' });
        scanCountsByDay.set(k, (scanCountsByDay.get(k) || 0) + 1);
      });
      filteredChats.forEach((c) => {
        const k = new Date(c.created_at).toLocaleDateString('en-US', { month: 'short', day: '2-digit' });
        chatCountsByDay.set(k, (chatCountsByDay.get(k) || 0) + 1);
      });

      const series = days.map((d) => {
        const k = d.toLocaleDateString('en-US', { month: 'short', day: '2-digit' });
        return {
          day: k,
          scans: scanCountsByDay.get(k) || 0,
          chats: chatCountsByDay.get(k) || 0,
        };
      });

      setScanSeries(series);
      setChatSeries(series);
    } catch (err) {
      console.error('Corporate Vitals dashboard fetch error:', err);
      setAlert('Failed to load dashboard data.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!enabled) return;
    if (!hasMounted.current && currentOrg) {
      fetchDashboard();
      hasMounted.current = true;
    }
  }, [enabled, currentOrg]);

  useEffect(() => {
    if (enabled && currentOrg) fetchDashboard();
  }, [enabled, timeRange, customFrom, customTo, currentOrg]);

  return {
    loading,
    timeRange,
    setTimeRange,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,

    matchedMindvaultIds,
    activeMindvaultIds,

    scanCount,
    chatCount,
    appointmentCount,
    scanSeries,
    chatSeries,
    riskCounts,

    role,
    userDetails,
    currentOrg,
  };
};

export default useDashboardDataVitals;